import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { loadStripe } from '@stripe/stripe-js/pure'
import { Elements } from '@stripe/react-stripe-js'
import { useSubscription } from '@/features/subscription/hooks/useSubscription'
import { useSubscriptionMutations } from '@/features/subscription/hooks/useSubscriptionMutations'
import { CardInputPlaceholder } from '@/features/subscription/components/CardInputPlaceholder'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Alert } from '@/components/ui/Alert'
import type { Plan } from '@/features/subscription/types'
import { useTranslation } from 'react-i18next'

const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined

/**
 * Le script de Stripe dépose des cookies tiers dès qu'il est chargé. Appelé au
 * niveau du module, il partait sur toutes les pages — y compris la vitrine,
 * pour des visiteurs qui n'atteindront jamais le paiement. Il n'est désormais
 * demandé qu'à l'ouverture de cette page, et une seule fois.
 */
let stripePromiseCache: ReturnType<typeof loadStripe> | null = null

function getStripe(): ReturnType<typeof loadStripe> | null {
  if (stripeKey === undefined || stripeKey === '') {
    return null
  }

  stripePromiseCache ??= loadStripe(stripeKey)

  return stripePromiseCache
}

const schema = z.object({
  cardholderName: z.string().min(1, 'subscription.cardholder_required'),
})

type FormData = z.infer<typeof schema>

function PageSkeleton() {
  return (
    <div className="mx-auto max-w-3xl animate-pulse px-4 py-10">
      <div className="mb-8 h-8 w-64 rounded bg-surface-100" />
      <div className="mb-6 h-10 w-56 rounded-full bg-surface-100" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="h-56 rounded-xl bg-surface-100" />
        <div className="h-64 rounded-xl bg-surface-100" />
      </div>
    </div>
  )
}

const PLANS: {
  id: Plan
  labelKey: string
  price: string
  billingKey: string
  billingValues?: Record<string, string>
  featureKeys: string[]
  popular: boolean
}[] = [
  {
    id: 'monthly',
    labelKey: 'subscription.monthly',
    price: '9,99 €',
    billingKey: 'subscription.billing_monthly',
    featureKeys: [
      'subscription.feature_full_access',
      'subscription.feature_unlimited_courses',
      'subscription.feature_personalised_ai',
    ],
    popular: false,
  },
  {
    id: 'annual',
    labelKey: 'subscription.annual',
    price: '7,99 €',
    billingKey: 'subscription.billing_annual',
    billingValues: { total: '95,88 €' },
    featureKeys: [
      'subscription.feature_full_access',
      'subscription.feature_unlimited_courses',
      'subscription.feature_personalised_ai',
      'subscription.feature_two_months_free',
    ],
    popular: true,
  },
]

export function SubscriptionPage() {
  return (
    <Elements stripe={getStripe()}>
      <SubscriptionForm />
    </Elements>
  )
}

function SubscriptionForm() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: subscription, isLoading } = useSubscription()
  const { createSubscription } = useSubscriptionMutations()
  const [selectedPlan, setSelectedPlan] = useState<Plan>('annual')
  // Le jeton vient d'un callback Stripe : le garder en état, plutôt qu'en ref,
  // évite qu'un rendu lise une valeur mutée hors du cycle React.
  const [stripeToken, setStripeToken] = useState('')

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema) })

  if (isLoading) return <PageSkeleton />
  if (subscription?.status === 'active') {
    return <Navigate to="/subscription/manage" replace />
  }

  const onSubmit = async (_data: FormData) => {
    await createSubscription.mutateAsync({
      plan: selectedPlan,
      stripeToken,
    })
    navigate('/subscription/manage')
  }

  const mutationError = createSubscription.error as Error | null

  return (
    <main className="min-h-screen bg-surface-50">
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        <h1 className="mb-2 text-2xl font-bold text-neutral-900">
          {t('subscription.choose_title')}
        </h1>
        <p className="mb-8 text-sm text-neutral-600">{t('subscription.choose_subtitle')}</p>

        {/* Plan toggle */}
        <div className="mb-6 inline-flex rounded-lg border border-surface-200 bg-white p-1">
          {PLANS.map((plan) => (
            <button
              key={plan.id}
              type="button"
              onClick={() => setSelectedPlan(plan.id)}
              className={[
                'min-h-[44px] rounded-md px-5 text-sm font-medium transition-colors',
                selectedPlan === plan.id
                  ? 'bg-primary-500 text-white shadow-sm'
                  : 'text-neutral-600 hover:text-neutral-900',
              ].join(' ')}
              aria-pressed={selectedPlan === plan.id}
            >
              {t(plan.labelKey)}
            </button>
          ))}
        </div>

        {/* Plan cards */}
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {PLANS.map((plan) => {
            const isSelected = selectedPlan === plan.id
            return (
              <div
                key={plan.id}
                role="radio"
                aria-checked={isSelected}
                tabIndex={0}
                onClick={() => setSelectedPlan(plan.id)}
                onKeyDown={(e) => e.key === 'Enter' && setSelectedPlan(plan.id)}
                className={[
                  'relative cursor-pointer rounded-xl border bg-white transition-shadow',
                  plan.popular ? 'p-6' : 'p-5',
                  isSelected
                    ? 'ring-2 ring-primary-500 border-primary-500 shadow-md'
                    : 'border-surface-200 hover:border-primary-500',
                ].join(' ')}
              >
                {plan.popular && (
                  <span className="mb-3 inline-block rounded-full bg-mindeia-accent px-3 py-0.5 text-xs font-semibold text-white">
                    {t('subscription.popular')}
                  </span>
                )}
                <p className="text-base font-semibold text-neutral-900">{t(plan.labelKey)}</p>
                <p className="mt-1">
                  <span className="text-3xl font-bold text-neutral-900">{plan.price}</span>
                  <span className="ml-1 text-sm text-neutral-600">
                    {t(plan.billingKey, plan.billingValues)}
                  </span>
                </p>
                <ul className="mt-4 space-y-2">
                  {plan.featureKeys.map((featureKey) => (
                    <li key={featureKey} className="flex items-center gap-2 text-sm text-neutral-700">
                      <svg
                        className="h-4 w-4 flex-shrink-0 text-mindeia-success"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {t(featureKey)}
                    </li>
                  ))}
                </ul>
              </div>
            )
          })}
        </div>

        {/* Subscription form */}
        <div className="rounded-xl border border-surface-200 bg-white p-6">
          <h2 className="mb-5 text-base font-semibold text-neutral-900">
            {t('subscription.payment_info')}
          </h2>
          {mutationError && (
            <Alert variant="error" className="mb-4">
              {mutationError.message ?? t('subscription.payment_error')}
            </Alert>
          )}
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
            <Input
              label={t('subscription.cardholder')}
              placeholder={t('subscription.cardholder_placeholder')}
              error={errors.cardholderName?.message && t(errors.cardholderName.message)}
              {...register('cardholderName')}
            />
            <div>
              <label className="mb-1 block text-sm font-medium text-surface-800">
                {t('subscription.card_details')}
              </label>
              <CardInputPlaceholder onToken={setStripeToken} />
            </div>
            <Button
              type="submit"
              isLoading={createSubscription.isPending}
              className="mt-2 w-full min-h-[44px]"
            >
              {t('subscription.subscribe_for', {
                price: PLANS.find((plan) => plan.id === selectedPlan)?.price ?? '',
              })}
            </Button>
          </form>
        </div>
      </div>
    </main>
  )
}
